import styled from '@emotion/styled';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { CircularProgress, FormHelperText, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/system';
import useLayoutTypeContext from 'framework/base/hooks/useLayoutTypeContext';
import useFrameworkTranslation from 'framework/language/useFrameworkTranslation';
import React, { useState } from 'react';
import Select, { components } from 'react-select';
import { debounce } from '../utils/Utils';

const Label = styled.label`
  right: 10px;
  pointer-events: none;
  position: absolute;
  z-index: 1;
  transition: 0.2s ease all;
  -moz-transition: 0.2s ease all;
  -webkit-transition: 0.2s ease all;
  background-color: ${(props) =>
    props.isfloating && props.theme.reactSelect.background.color};
  padding: ${(props) => props.isfloating && '0 5px'};
  color: ${(props) =>
    props.isfocused
      ? props.theme.palette.primary.main
      : props.theme.textField.label.color};
  top: ${(props) => (props.isfloating ? '-9px' : 'calc(50% - 14px)')};
  font-size: ${(props) =>
    props.isfloating ? '.625rem' : props.theme.typography.h6Medium.fontSize};
  font-weight: ${(props) => props.theme.typography.h6Medium.fontWeight};
  line-height: ${(props) => !props.isfloating && '28px'};
`;

function AryLazyAutoCompleteBase({
  multiple,
  loadOptions = () => {},
  enableAutoFilter,
  label,
  disabled,
  maxheight,
  style,
  size = 'normal',
  error,
  helperText,
  noOptionsMessage,
  autoFocus,
  loading,
  handleMoeExist = () => {},
  options = [],
  ...others
}) {
  const { t } = useFrameworkTranslation();
  const theme = useTheme();
  const [pageIndex, setPageIndex] = useState(0);
  const [inputValue, setInputValue] = useState('');
  const layoutType = useLayoutTypeContext();

  const SelectItemLoading = () => (
    <Box display="flex" alignItems="center" justifyContent="center">
      <CircularProgress size={16} />
      <Typography color={theme.textField.label.color} sx={{ mx: 2 }}>
        {t('In progress')}
      </Typography>
    </Box>
  );

  const NoOptionMessage = () => (
    <Typography color={theme.textField.label.color}>
      {t('No Content Message')}
    </Typography>
  );

  const handleResetSearchValue = () => {
    loadOptions(0, '');
    setInputValue('');
    setPageIndex(0);
    handleMoeExist(true);
  };

  function getBackgroundColor(state) {
    if (!state) return null;
    if (loading) return theme.reactSelect.background.color;
    if (state.isSelected) {
      return theme.reactSelect.selectedOption.backgroundColor;
    }
    if (state.isFocused) {
      return theme.reactSelect.hoveredOption.backgroundColor;
    }
    return theme.reactSelect.background.color;
  }

  const DropdownIndicator = (props) => {
    const { selectProps } = props;

    const iconStyle = {
      color: error ? theme.palette.error.main : theme.reactSelect.icon.color
    };

    return (
      <components.DropdownIndicator layouttype={layoutType} {...props}>
        {selectProps.menuIsOpen ? (
          <ArrowDropUp sx={iconStyle} />
        ) : (
          <ArrowDropDown sx={iconStyle} />
        )}
      </components.DropdownIndicator>
    );
  };

  const Control = ({ children, ...props }) => {
    const { isFocused, hasValue } = props;

    return (
      <components.Control {...props}>
        <Label isfloating={isFocused || hasValue} isfocused={isFocused}>
          {label}
        </Label>
        {children}
      </components.Control>
    );
  };

  const handleInputChange = (newValue, e) => {
    if (e.action === 'input-change' || e.action === 'set-value') {
      debounce(() => loadOptions(0, newValue));
      setInputValue(newValue);
      setPageIndex(0);
      handleMoeExist(true);
      return newValue;
    }
    if (e.action === 'menu-close') {
      handleResetSearchValue();
    }
    return undefined;
  };

  const handleOnScrollEnd = () => {
    loadOptions(pageIndex + 1, inputValue);
    setPageIndex((...prev) => +prev + 1);
  };

  const myComponents = () => {
    if (multiple) {
      return { DropdownIndicator };
    }
    if (!multiple) {
      return { DropdownIndicator };
    }
    return { DropdownIndicator, Control };
  };

  return (
    <>
      <Select
        noOptionsMessage={() => noOptionsMessage || <NoOptionMessage />}
        captureMenuScroll
        autoFocus={autoFocus}
        onMenuScrollToBottom={(e) => {
          if (
            e.target.offsetHeight + e.target.scrollTop >=
            e.target.scrollHeight
          ) {
            handleOnScrollEnd();
          }
        }}
        onInputChange={handleInputChange}
        filterOption={enableAutoFilter ? undefined : (i) => i}
        options={options}
        isDisabled={disabled}
        placeholder={label}
        components={myComponents()}
        isMulti={multiple}
        closeMenuOnSelect={!multiple}
        loadingMessage={() => <SelectItemLoading />}
        isLoading={loading}
        styles={{
          container: (provided) => ({
            ...provided,
            maxWidth: '100%',
            ...style?.container
          }),
          control: (provided, state) => ({
            ...provided,
            maxWidth: '100%',
            background:
              theme.name === 'DARK' && theme.reactSelect.background.color,
            minHeight:
              size === 'small'
                ? theme.componentSizes.filterFieldHeight
                : layoutType === 'mobile'
                ? '48px'
                : '2rem',
            height: size === 'small' && theme.componentSizes.filterFieldHeight,
            boxShadow: 'none',
            borderWidth: others.isDraftConduction
              ? '0'
              : state.isFocused
              ? '2px'
              : '1px',
            'border-bottom-width': others.isDraftConduction ? '2px' : 'auto',
            borderColor: error
              ? theme.palette.error.main
              : state.isFocused
              ? theme.palette.primary.main
              : theme.reactSelect.border.color,
            '&:hover': {
              borderColor: error
                ? theme.palette.error.main
                : !state.isFocused
                ? others.isDraftConduction
                  ? 'rgba(0, 119, 182, 0.4)'
                  : theme.text.primary.color
                : 'auto'
            },
            marginBottom: state.selectProps.menuIsOpen ? style?.marginBottom && style.marginBottom : '0',
            ...style?.control
          }),
          valueContainer: (provided) => ({
            ...provided,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            ...style?.valueContainer
          }),
          input: (provided) => ({
            ...provided,
            color: theme.text.primary.color,
            fontSize:
              layoutType === 'laptop' || layoutType === 'tablet'
                ? '0.75rem'
                : layoutType === 'desktop'
                ? '0.875rem'
                : '12px',
            fontWeight: 500,
            lineHeight:
              layoutType === 'laptop' || layoutType === 'tablet'
                ? '.875rem'
                : layoutType === 'desktop'
                ? '1rem'
                : '14px',
            ...style?.input
          }),
          option: (provided, state) => ({
            ...provided,
            color: theme.text.primary.color,
            padding: layoutType === 'mobile' ? '0 16px' : '0 1rem',
            minHeight:
              layoutType === 'desktop'
                ? '2.375rem'
                : layoutType === 'laptop' || layoutType === 'tablet'
                ? '1.875rem'
                : '38px',
            backgroundColor: getBackgroundColor(state),
            display: 'flex',
            alignItems: 'center',
            fontSize:
              layoutType === 'desktop'
                ? '0.875rem'
                : layoutType === 'laptop' || layoutType === 'tablet'
                ? '0.75rem'
                : '12px',
            lineHeight:
              layoutType === 'desktop'
                ? '2.375rem'
                : layoutType === 'laptop' || layoutType === 'tablet'
                ? '1.875rem'
                : '38px',
            '&:hover': {
              backgroundColor: loading
                ? theme.reactSelect.background.color
                : theme.reactSelect.hoveredOption.backgroundColor
            },
            ...style?.option
          }),
          noOptionsMessage: (provided) => ({
            ...provided,
            padding: layoutType === 'desktop' ? '.5rem .75rem' : 0,
            ...style?.noOptionsMessage
          }),
          loadingMessage: (provided) => ({
            ...provided,
            padding: layoutType === 'desktop' ? '.5rem .75rem' : 0,
            ...style?.loadingMessage
          }),
          clearIndicator: (provided) => ({
            ...provided,
            padding: 0,
            ...style?.noOptionsMessage
          }),
          placeholder: (provided) => ({
            ...provided,
            color: error
              ? theme.palette.error.main
              : theme.reactSelect.placeholder.color,
            fontSize:
              layoutType === 'laptop' || layoutType === 'tablet'
                ? '0.75rem'
                : layoutType === 'desktop'
                ? '0.875rem'
                : '12px',
            fontWeight: 500,
            lineHeight:
              layoutType === 'laptop' || layoutType === 'tablet'
                ? '.875rem'
                : layoutType === 'desktop'
                ? '1rem'
                : '14px',
            ...style?.placeholder
          }),
          singleValue: (provided) => ({
            ...provided,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            maxWidth: 'calc(100% - 8px)',
            color: theme.text.primary.color,
            fontSize:
              layoutType === 'laptop' || layoutType === 'tablet'
                ? '0.75rem'
                : layoutType === 'desktop'
                ? '0.875rem'
                : '12px',
            fontWeight: 500,
            lineHeight:
              layoutType === 'laptop' || layoutType === 'tablet'
                ? '.875rem'
                : layoutType === 'desktop'
                ? '1rem'
                : '14px',
            ...style?.singleValue
          }),
          menu: (provided) => ({
            ...provided,
            minWidth: '100%',
            width: 'max-content',
            maxWidth: '90vw',
            background: theme.reactSelect.background.color,
            marginTop: layoutType === 'mobile' ? '5px' : '.313rem',
            padding: layoutType === 'mobile' ? '4px 0' : '.25rem 0',
            borderRadius: layoutType === 'mobile' ? '4px' : '.25rem',
            boxShadow:
              '0px .25rem 1rem .25rem rgba(0, 0, 0, 0.12), 0px .25rem .25rem rgba(0, 0, 0, 0.25), 0px .25rem .75rem 0 rgba(0, 0, 0, 0.14)',
            zIndex: 2,
            ...style?.menu
          }),
          menuList: (provided) => ({
            ...provided,
            maxHeight:
              maxheight || (layoutType === 'mobile' ? '230px' : '14.375rem'),
            ...style?.menuList
          }),
          dropdownIndicator: (provided) => ({
            ...provided,
            display: others.isDraftConduction ? 'none' : 'flex',
            padding:
              layoutType === 'desktop'
                ? '0 .5rem'
                : layoutType === 'laptop' || layoutType === 'tablet'
                ? '0 .25rem'
                : '0 8px',
            ...style?.dropdownIndicator
          }),
          indicatorSeparator: () => null
        }}
        {...others}
      />
      {!!helperText && (
        <FormHelperText error={error}>{helperText}</FormHelperText>
      )}
    </>
  );
}

export default AryLazyAutoCompleteBase;
